import React from 'react';
import { LineChart } from '@mui/x-charts/LineChart';
import Skeleton from '@mui/material/Skeleton';
import { colours } from 'utils/constants';
import NoData from 'components/elements/Reports/NoData';
import { ForecastPeriod } from 'Types/ForecastFrequency';

interface PredictionGraphProps {
  pastData: {
    dates: Date[];
    values: number[];
  };
  futureData: {
    dates: Date[];
    values: number[];
  };
  capacity: number;
  loading: boolean;
  showMaxCapacity: boolean;
  forecastFrequency: ForecastPeriod;
}

export default function PredictionGraph({
  pastData,
  futureData,
  capacity,
  loading,
  showMaxCapacity,
  forecastFrequency
}: PredictionGraphProps) {
  const GRAPH_HEIGHT = 300;

  if (loading) {
    return <Skeleton variant="rectangular" height={GRAPH_HEIGHT} />;
  }

  if (!pastData.dates.length && !futureData.dates.length) {
    return <NoData height={GRAPH_HEIGHT} hint="Consider updating your filters" />;
  }

  // Prepend zeros to future data values to align with past data
  const futureDataWithZeros: number[] = new Array(pastData.dates.length)
    .fill(0)
    .concat(futureData.values);

  // Calculate percentage occupancy for past and future data
  const pastRelativeData = pastData.values.map((occupant) => (100 * occupant) / (capacity || 1));
  const futureRelativeData = futureDataWithZeros.map(
    (occupant) => (100 * occupant) / (capacity || 1)
  );

  // Calculate the maximum occupancy value
  const maxOccupancy = Math.max(...pastData.values, ...futureData.values);

  // Determine today's date
  const today = new Date();
  const combinedDates = pastData.dates.concat(futureData.dates);

  // Format dates if forecast frequency is yearly
  const formattedDates =
    forecastFrequency === ForecastPeriod.Yearly
      ? combinedDates.map((date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()))
      : combinedDates;

  const todayIndex = formattedDates.findIndex((date) => {
    return new Date(date).toDateString() === today.toDateString();
  });

  const series = [
    {
      id: 'past-occupants',
      label: 'Past Occupancy',
      data: pastData.values,
      area: true,
      showMark: false,
      color: colours.seafoam,
      yAxisKey: 'absolute'
    },
    {
      id: 'future-occupants',
      label: 'Future Occupancy',
      data: futureDataWithZeros,
      area: true,
      showMark: false,
      color: colours.predictionGhostGrey,
      yAxisKey: 'absolute'
    }
  ];

  if (todayIndex !== -1) {
    const currentDateLineData = new Array(todayIndex).fill(0).concat(maxOccupancy);
    series.push({
      id: 'Today',
      label: 'Today',
      data: currentDateLineData,
      area: false,
      showMark: false,
      color: 'black',
      yAxisKey: 'absolute'
    });
  }

  if (showMaxCapacity) {
    const capacityValues = new Array(pastData.dates.length + futureData.dates.length).fill(
      capacity
    );
    series.push({
      id: 'capacity',
      label: 'Max capacity',
      data: capacityValues,
      area: false,
      showMark: false,
      color: 'red',
      yAxisKey: 'absolute'
    });
  }

  return (
    <div style={{ marginLeft: 20 }}>
      <LineChart
        sx={{
          '& .MuiLineElement-series-past-occupants': {
            stroke: colours.seafoam
          },
          '& .MuiLineElement-series-future-occupants': {
            stroke: colours.predictionGhostGrey
          },
          '& .MuiLineElement-root': { strokeWidth: 1 },
          '& .MuiChartsAxis-bottom .MuiChartsAxis-line': {
            stroke: colours.axisGrey,
            strokeWidth: 0.4
          },
          '& .MuiChartsAxis-left .MuiChartsAxis-line': {
            stroke: colours.axisGrey,
            strokeWidth: 0.4
          },
          '& .MuiChartsAxis-right .MuiChartsAxis-line': {
            stroke: colours.axisGrey,
            strokeWidth: 0.4
          },
          '& .MuiChartsAxis-tick': { stroke: colours.axisGrey, strokeWidth: 0.4 },
          '& .MuiChartsAxis-root .MuiChartsAxis-label': { fill: colours.axisGrey },
          '& .MuiChartsAxis-tickContainer .MuiChartsAxis-tickLabel': { fill: colours.axisGrey },
          '& .MuiChartsAxis-left .MuiChartsAxis-label': { transform: 'translate(-11px, 0)' },
          '& .MuiLineElement-series-Today': { strokeDasharray: '5, 5' }
        }}
        xAxis={[
          {
            data: formattedDates,
            scaleType: 'time',
            label: 'Date'
          }
        ]}
        yAxis={[
          {
            id: 'absolute',
            data: pastData.values.concat(futureDataWithZeros),
            label: 'Occupants',
            valueFormatter: (numOccupants: number) => `${numOccupants}`
          },
          {
            id: 'relative',
            data: pastRelativeData.concat(futureRelativeData),
            label: '',
            valueFormatter: (occupancy: number) => `${occupancy}%`,
            max: showMaxCapacity
              ? Math.max(100, Math.floor(100 * maxOccupancy) / (capacity || 1))
              : Math.floor((100 * maxOccupancy) / (capacity || 1))
          }
        ]}
        series={series}
        leftAxis="absolute"
        rightAxis="relative"
        height={GRAPH_HEIGHT}
        slotProps={{
          legend: {
            direction: 'row'
          }
        }}
      >
        <defs>
          <linearGradient id="lineGradientPast" gradientTransform="rotate(90)">
            <stop offset="1%" stopColor={colours.darkTeal} />
            <stop offset="99%" stopColor={colours.lightTeal} />
          </linearGradient>
          <linearGradient id="lineGradientFuture" gradientTransform="rotate(90)">
            <stop offset="1%" stopColor={colours.predictionGhostGrey} />
            <stop offset="99%" stopColor={colours.predictionGhostGrey} />
          </linearGradient>
        </defs>
      </LineChart>
    </div>
  );
}
